import { Check, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import {
  Checkbox,
  CheckboxGroup,
  Label,
  Radio,
  RadioGroup,
  Text as AriaText,
} from 'react-aria-components';
import { Icon } from '../atoms/Icon';
import { cn } from '../lib/cn';

export interface Choice<V extends string = string> {
  value: V;
  label: string;
  icon?: PhosphorIcon;
  /** Custom picture (e.g. an avatar) instead of an icon. */
  picture?: ReactNode;
  description?: string;
}

const chipBase = cn(
  'group relative flex min-h-touch-min cursor-pointer select-none items-center gap-gap-sm rounded-control border-2 font-semibold outline-none',
  'text-text-primary transition duration-(--shh-motion-duration-quick) data-pressed:scale-97',
  'data-selected:border-action-primary data-selected:bg-accent-secondary-soft',
  'data-focus-visible:outline-3 data-focus-visible:outline-offset-2 data-focus-visible:outline-border-focus',
  'data-disabled:cursor-not-allowed data-disabled:opacity-50',
);
type Layout = 'row' | 'tile' | 'person';
/** person = a quiet row of avatars (1.25 forms): no frame until picked, so it reads as one field */
const chip = (layout: Layout) =>
  cn(
    chipBase,
    layout === 'person'
      ? 'shrink-0 flex-col gap-gap-xs border-transparent px-inset-xs py-inset-xs text-sm'
      : 'border-border-subtle bg-surface-raised data-hovered:border-border-default',
    layout === 'row' && 'px-inset-sm py-inset-xs',
    layout === 'tile' && 'flex-col px-inset-xs py-inset-xs',
  );

/**
 * Several of several get a small tick box (Mike, 20.10.: "es ist im UI nicht klar, was sind
 * Einzelauswahl und was sind Mehrfachauswahlfelder") – one of several stays without.
 */
function TickBox({ layout }: { layout: Layout }) {
  const corner = layout !== 'row';
  return (
    <span
      aria-hidden
      className={cn(
        'grid shrink-0 place-items-center text-transparent',
        'group-data-selected:border-action-primary group-data-selected:bg-action-primary group-data-selected:text-text-on-action',
        // on a tile the box sits in the corner, clear of the picture – small, round and faint
        // until chosen (Mike, 21.10.: "Häkchen-Kästchen auf Avatar ruhiger")
        corner
          ? 'absolute top-0.5 right-0.5 size-4 rounded-full border border-border-subtle'
          : 'size-5 rounded-sm border-2 border-border-default bg-surface-raised',
      )}
    >
      {corner ? (
        // below the smallest icon token on purpose: a mark, not a control
        <Check weight="bold" className="size-3" />
      ) : (
        <Icon icon={Check} size="sm" weight="bold" />
      )}
    </span>
  );
}

function ChoiceContent({ c, layout, multi }: { c: Choice; layout: Layout; multi?: boolean }) {
  return (
    <>
      {/* people (pictures) go without ticks, like in the calendar (Mike, 21.10.: "bei Personen bitte
          ohne Checkboxen"; 25.10.: "gleichziehen") */}
      {multi && layout !== 'person' && !c.picture && <TickBox layout={layout} />}
      {c.picture ?? (c.icon && <Icon icon={c.icon} size={layout === 'tile' ? 'lg' : 'md'} />)}
      <span className={cn('flex flex-col', layout !== 'row' && 'items-center text-center')}>
        <span>{c.label}</span>
        {c.description && (
          <span className="text-xs font-medium text-text-secondary">{c.description}</span>
        )}
      </span>
    </>
  );
}

export interface ChoiceGroupProps<V extends string> {
  label: string;
  description?: string;
  choices: Choice<V>[];
  value: V;
  onChange: (v: V) => void;
  /** tile = larger, centred (pictures, people) */
  layout?: Layout;
  className?: string;
}

/** One of several – a radio group that looks like large chips. Arrow keys work. */
export function ChoiceGroup<V extends string>({
  label,
  description,
  choices,
  value,
  onChange,
  layout = 'row',
  className,
}: ChoiceGroupProps<V>) {
  return (
    <RadioGroup
      value={value}
      onChange={(v) => onChange(v as V)}
      orientation="horizontal"
      className={cn('flex flex-col gap-gap-sm', className)}
    >
      <Label className="text-sm font-semibold text-text-secondary">{label}</Label>
      {description && (
        <AriaText slot="description" className="-mt-gap-xs text-sm text-text-muted">
          {description}
        </AriaText>
      )}
      <div
        className={cn(
          'flex',
          // people in one row to swipe through (Mike, 20.10.: "alle nebeneinander")
          layout === 'person'
            ? '-mx-inset-xs gap-gap-xs overflow-x-auto px-inset-xs'
            : 'flex-wrap gap-gap-sm',
        )}
      >
        {choices.map((c) => (
          <Radio key={c.value} value={c.value} className={chip(layout)}>
            <ChoiceContent c={c} layout={layout} />
          </Radio>
        ))}
      </div>
    </RadioGroup>
  );
}

export interface MultiChoiceProps<V extends string> {
  label: string;
  description?: string;
  choices: Choice<V>[];
  value: V[];
  onChange: (v: V[]) => void;
  layout?: Layout;
  className?: string;
}

/** Several of several – a checkbox group as chips (weekdays, people). */
export function MultiChoice<V extends string>({
  label,
  description,
  choices,
  value,
  onChange,
  layout = 'row',
  className,
}: MultiChoiceProps<V>) {
  return (
    <CheckboxGroup
      value={value}
      onChange={(v) => onChange(v as V[])}
      className={cn('flex flex-col gap-gap-sm', className)}
    >
      <Label className="text-sm font-semibold text-text-secondary">{label}</Label>
      {description && (
        <AriaText slot="description" className="-mt-gap-xs text-sm text-text-muted">
          {description}
        </AriaText>
      )}
      <div
        className={cn(
          'flex',
          // people in one row to swipe through (Mike, 20.10.: "alle nebeneinander")
          layout === 'person'
            ? '-mx-inset-xs gap-gap-xs overflow-x-auto px-inset-xs'
            : 'flex-wrap gap-gap-sm',
        )}
      >
        {choices.map((c) => (
          <Checkbox key={c.value} value={c.value} className={chip(layout)}>
            <ChoiceContent c={c} layout={layout} multi />
          </Checkbox>
        ))}
      </div>
    </CheckboxGroup>
  );
}
